import { useCallback, useContext, useMemo } from 'react';

import { useFormikContext } from 'formik';

import { Deal } from '../gql/dealGql';
import {
  PaymentOptionsPackageV2,
  ProductSlug,
  usePaymentOptionsV2Query,
} from '../gql/generated/graphql';

import { paymentOptionsTypes } from '../components/DealInfoBuyout/types';
import { DealContext } from '../libs/DealContext';
import { PackageConfig, PackagePaymentResult } from '../types/products';
import { isSellableTerm } from '../utils/financialInfos';

type PaymentOptionsMap = {
  vscPlusGap: paymentOptionsTypes;
  vsc: paymentOptionsTypes;
  gap: paymentOptionsTypes;
  noProducts: paymentOptionsTypes;
};

const toPaymentOption = (option: {
  name?: string | null;
  amountFinanced?: number | null;
  buyPayment?: number | null;
  sellPayment?: number | null;
  profit?: number | null;
  setterCommission?: number | null;
  closerCommission?: number | null;
  reserve?: number | null;
}): paymentOptionsTypes => ({
  name: (option.name as paymentOptionsTypes['name']) || 'noProducts',
  amountFinanced: option.amountFinanced ?? 0,
  buyPayment: option.buyPayment ?? 0,
  sellPayment: option.sellPayment ?? 0,
  profit: option.profit ?? 0,
  setterCommission: option.setterCommission ?? 0,
  closerCommission: option.closerCommission ?? 0,
  reserve: option.reserve ?? 0,
});

const toPackagePaymentResult = (
  packageConfig: PackageConfig,
  packageResult: PaymentOptionsPackageV2,
): PackagePaymentResult => ({
  packageId: packageConfig.id,
  amountFinanced: packageResult.amountFinanced,
  buyPayment: packageResult.buyPayment,
  sellPayment: packageResult.sellPayment,
  profit: packageResult.profit,
  setterCommission: packageResult.setterCommission,
  closerCommission: packageResult.closerCommission,
  reserve: packageResult.reserve,
  products: packageResult.products.map((product) => ({
    slug: product.slug,
    price: product.price,
    cost: product.cost,
  })),
});

export const usePackagePayments = (packageConfigs: PackageConfig[]) => {
  const { deal } = useContext(DealContext);
  const dealId = deal?.id ? Number(deal.id) : 0;

  const { values } = useFormikContext<Deal>();

  const vehiclePayoff = values.car.payoff?.vehicle_payoff;
  const carYear = values.car.year;
  const carMileage = values.car.mileage;
  const prequalificationCreditScore = values.customer.prequalification?.credit_score;
  const {
    money_down,
    bank_fees,
    user_entered_reserve,
    warranty_tax_amount,
    base_tax_amount,
    title_registration_option,
    title_fee,
    new_registration_fee,
    registration_transfer_fee,
    doc_fee,
    buy_rate,
    sell_rate,
    term,
    days_to_payment,
    bank,
    vsc_price,
    vsc_cost,
    gap_price,
    gap_cost,
  } = values.financial_info;

  const { tire_wheel_price, tire_wheel_cost, key_replacement_price, key_replacement_cost } = values;
  const tier = values.financial_info.selected_credit_decision?.tier;

  const queryInput = useMemo(
    () => ({
      dealId,
      overrides: {
        vehiclePayoff,
        moneyDown: money_down,
        bankFees: bank_fees,
        userEnteredReserve: user_entered_reserve,
        warrantyTaxAmount: warranty_tax_amount,
        baseTaxAmount: base_tax_amount,
        titleRegistrationOption: title_registration_option,
        titleFee: title_fee,
        newRegistrationFee: new_registration_fee,
        registrationTransferFee: registration_transfer_fee,
        docFee: doc_fee,
        buyRate: buy_rate,
        sellRate: sell_rate,
        // Don't send a term we could never sell; the server falls back to the deal's own.
        term: isSellableTerm(term) ? term : undefined,
        tier,
        daysToPayment: days_to_payment,
        bank,
        carYear: carYear ? Number.parseInt(carYear, 10) : undefined,
        carMileage,
        prequalificationCreditScore,
        productOverrides: [
          {
            productSlug: ProductSlug.Vsc,
            price: vsc_price ?? undefined,
            cost: vsc_cost ?? undefined,
            taxAmount: warranty_tax_amount ?? undefined,
          },
          {
            productSlug: ProductSlug.Gap,
            price: gap_price ?? undefined,
            cost: gap_cost ?? undefined,
          },
          {
            productSlug: ProductSlug.TireWheel,
            price: tire_wheel_price ?? undefined,
            cost: tire_wheel_cost ?? undefined,
          },
          {
            productSlug: ProductSlug.KeyReplacement,
            price: key_replacement_price ?? undefined,
            cost: key_replacement_cost ?? undefined,
          },
        ],
      },
      packageConfigs: packageConfigs.map((config) => ({
        slotIndex: config.slotIndex,
        selectedProductSlugs: config.selectedProductSlugs,
      })),
    }),
    [
      dealId,
      packageConfigs,
      vehiclePayoff,
      carYear,
      carMileage,
      prequalificationCreditScore,
      money_down,
      bank_fees,
      user_entered_reserve,
      warranty_tax_amount,
      base_tax_amount,
      title_registration_option,
      title_fee,
      new_registration_fee,
      registration_transfer_fee,
      doc_fee,
      buy_rate,
      sell_rate,
      term,
      tier,
      days_to_payment,
      bank,
      vsc_price,
      vsc_cost,
      gap_price,
      gap_cost,
      tire_wheel_price,
      tire_wheel_cost,
      key_replacement_price,
      key_replacement_cost,
    ],
  );

  const { data, previousData, loading, refetch } = usePaymentOptionsV2Query({
    variables: { input: queryInput },
    skip: !dealId,
    fetchPolicy: 'cache-and-network',
  });

  const paymentOptionsData = data?.paymentOptionsV2 ?? previousData?.paymentOptionsV2;

  const paymentOptions: PaymentOptionsMap = useMemo(
    () => ({
      vscPlusGap: toPaymentOption(paymentOptionsData?.vscPlusGap ?? {}),
      vsc: toPaymentOption(paymentOptionsData?.vsc ?? {}),
      gap: toPaymentOption(paymentOptionsData?.gap ?? {}),
      noProducts: toPaymentOption(paymentOptionsData?.noProducts ?? {}),
    }),
    [paymentOptionsData],
  );

  const packageResultsBySlot = useMemo(
    () =>
      new Map(
        (paymentOptionsData?.packages ?? []).map((packageResult) => [
          packageResult.slotIndex,
          packageResult,
        ]),
      ),
    [paymentOptionsData],
  );

  const payments = useMemo(
    () =>
      packageConfigs.map((packageConfig) => {
        const packageResult = packageResultsBySlot.get(packageConfig.slotIndex);
        if (!packageResult) {
          return {
            packageId: packageConfig.id,
            amountFinanced: 0,
            buyPayment: 0,
            sellPayment: 0,
            profit: 0,
            setterCommission: 0,
            closerCommission: 0,
            reserve: 0,
            products: packageConfig.selectedProductSlugs.map((slug) => ({
              slug,
              price: 0,
              cost: 0,
            })),
          };
        }
        return toPackagePaymentResult(packageConfig, packageResult);
      }),
    [packageConfigs, packageResultsBySlot],
  );

  const getPackageResult = useCallback(
    (packageConfig: PackageConfig): PaymentOptionsPackageV2 | undefined =>
      packageResultsBySlot.get(packageConfig.slotIndex),
    [packageResultsBySlot],
  );

  const getPaymentByPackageId = useCallback(
    (packageId: string): PackagePaymentResult | undefined =>
      payments.find((payment) => payment.packageId === packageId),
    [payments],
  );

  const getOptionForPackage = useCallback(
    (packageConfig: PackageConfig): paymentOptionsTypes => {
      const packageResult = packageResultsBySlot.get(packageConfig.slotIndex);
      if (!packageResult) {
        return paymentOptions.noProducts;
      }

      return toPaymentOption({ ...packageResult, name: packageResult.legacyOptionType });
    },
    [packageResultsBySlot, paymentOptions.noProducts],
  );

  return {
    payments,
    paymentOptions,
    isLoading: loading,
    isDebouncing: false,
    selectedPackageSlotIndex: paymentOptionsData?.selectedPackageSlotIndex ?? null,
    getPackageResult,
    getPaymentByPackageId,
    getOptionForPackage,
    refetch,
  };
};
